/**
 * customerStanding — is a contact a Lead, a Customer or a Past Customer.
 *
 * MIRROR of backend/functions/src/leads/customerStanding.ts (the backend cannot import from
 * app/shared). Both run __fixtures__/customerStandingCases.json, so a case that disagrees
 * fails on whichever side is wrong.
 *
 * `relationship` stays lead | customer; a customer whose last service is more than
 * the CLIENT's `pastCustomerAfterDays` ago (default PAST_CUSTOMER_AFTER_DAYS, bounds
 * 90-3650, written only by the backend's leads/pastCustomerSetting.ts) reads as past, unless the OWNER said otherwise
 * (`customerStanding: 'past' | 'current'`, written only by the backend relationship writer).
 */

export type CustomerStanding = 'lead' | 'customer' | 'past-customer'

export const PAST_CUSTOMER_AFTER_DAYS = 730
/** The client's own setting must be a whole number of days inside these bounds. */
export const PAST_CUSTOMER_AFTER_DAYS_MIN = 90
export const PAST_CUSTOMER_AFTER_DAYS_MAX = 3650
const DAY_MS = 86_400_000

/** PURE — is this a usable setting (a whole number of days inside the bounds)? */
export function isValidPastCustomerAfterDays(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= PAST_CUSTOMER_AFTER_DAYS_MIN && v <= PAST_CUSTOMER_AFTER_DAYS_MAX
}

/** PURE — the client's setting, or the default when unset / unusable. */
export function pastCustomerAfterDaysOf(client: { pastCustomerAfterDays?: unknown } | null | undefined): number {
  const v = client?.pastCustomerAfterDays
  return isValidPastCustomerAfterDays(v) ? v : PAST_CUSTOMER_AFTER_DAYS
}

/** PURE — a number of days in the owner's words: "2 years", "18 months", "100 days". */
export function pastCustomerAfterWords(days: number): string {
  const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`
  // Within a day of a whole year / month reads as that (182 days is "6 months").
  const years = Math.round(days / 365)
  if (years > 0 && Math.abs(years * 365 - days) <= 1) return plural(years, 'year')
  const months = Math.round((days * 12) / 365)
  if (months > 0 && Math.abs(Math.round((months * 365) / 12) - days) <= 1) return plural(months, 'month')
  return plural(days, 'day')
}

/** PURE — an amount the owner said, in days (a month = 365/12 days, rounded). Null = not usable. */
export function daysFromAmount(amount: number, unit: string): number | null {
  if (!Number.isFinite(amount) || amount <= 0) return null
  const u = String(unit).toLowerCase().replace(/s$/, '')
  if (u === 'year') return Math.round(amount * 365)
  if (u === 'month') return Math.round((amount * 365) / 12)
  if (u === 'week') return Math.round(amount * 7)
  if (u === 'day') return Math.round(amount)
  return null
}

export interface StandingInput {
  relationship?: unknown
  customerStanding?: unknown
  serviceCompletedAt?: unknown
  lastServiceDate?: unknown
}

/** PURE — epoch ms of a Firestore Timestamp, {seconds}, Date, ISO string or number; null when none. */
export function standingMs(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isFinite(t) ? t : null }
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v.getTime() : null
  const o = v as { toMillis?: () => number; seconds?: unknown; _seconds?: unknown }
  if (typeof o.toMillis === 'function') return o.toMillis()
  const sec = typeof o.seconds === 'number' ? o.seconds : typeof o._seconds === 'number' ? o._seconds : null
  return sec === null ? null : sec * 1000
}

/** PURE — the last service on record, or null. */
export function lastServiceMs(lead: StandingInput): number | null {
  const a = standingMs(lead.serviceCompletedAt)
  const b = standingMs(lead.lastServiceDate)
  if (a === null) return b
  if (b === null) return a
  return Math.max(a, b)
}

/**
 * PURE — lead / customer / past customer, as every surface shows it. `afterDays` is the
 * CLIENT's setting (`pastCustomerAfterDaysOf(client)`); an unusable value falls back to the
 * default, so a bad stored number can never make every customer read as past.
 */
export function customerStandingOf(lead: StandingInput, nowMs: number, afterDays: unknown = PAST_CUSTOMER_AFTER_DAYS): CustomerStanding {
  if (lead.relationship !== 'customer') return 'lead'
  if (lead.customerStanding === 'past') return 'past-customer'
  if (lead.customerStanding === 'current') return 'customer'
  const days = isValidPastCustomerAfterDays(afterDays) ? afterDays : PAST_CUSTOMER_AFTER_DAYS
  const last = lastServiceMs(lead)
  return last !== null && nowMs - last > days * DAY_MS ? 'past-customer' : 'customer'
}
